import { redirect, LoaderFunctionArgs } from "react-router-dom";
import { validateToken } from "utils/checkAuthorization";
import { getUserDetails } from "utils/localStorageServices";
import { withRedirectParam } from "utils/redirectPath";

const MEET_FLAG_KEYS = ["meet", "meetAddon", "isMeetAddon"];
const MEET_SOURCE_KEYS = ["source", "launchSource", "context"];
const MEET_SOURCE_VALUES = ["meet", "google-meet", "google_meet", "meet-addon"];

function safeLocalStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function isMeetRequest(url: URL): boolean {
  const hasMeetSdk = Boolean((url.searchParams.get("meet_sdk") || "").trim());
  const hasMeetFlag = MEET_FLAG_KEYS.some((key) => {
    const value = url.searchParams.get(key);
    return value === "1" || value === "true";
  });
  const hasMeetSource = MEET_SOURCE_KEYS.some((key) =>
    MEET_SOURCE_VALUES.includes((url.searchParams.get(key) || "").toLowerCase()),
  );

  return hasMeetSdk || hasMeetFlag || hasMeetSource || url.pathname.startsWith("/meet-addon/");
}

/**
 * Loader applied to all protected app/admin routes.
 * - Redirects unauthenticated users to /login
 * - Redirects users who have not completed onboarding to /onboarding
 */
export function protectedRouteLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const redirectTo = encodeURIComponent(`${url.pathname}${url.search}`);
  const userDetails = getUserDetails();
  const isAuthenticated = userDetails?.token && validateToken();

  if (!isAuthenticated) {
    return redirect(`/login?redirect=${redirectTo}`);
  }

  // Only redirect when we explicitly know the user hasn't completed onboarding.
  // Treat null (missing flag) as trusted — it means either the user is an existing
  // onboarded user from before this feature shipped, or localStorage was cleared.
  const onboardingComplete = safeLocalStorageGet("onboardingComplete");
  if (onboardingComplete === "false" && !isMeetRequest(url)) {
    // Keep the requested route so onboarding can hand the user back to it.
    return redirect(
      withRedirectParam("/onboarding", `${url.pathname}${url.search}`),
    );
  }

  return null;
}
